import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import {
  Search,
  Filter,
  Plus,
  Trash2,
  Eye,
  X,
  RefreshCw,
  Building2,
  FileSpreadsheet,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import { ProjectEditModal } from '@/components/ProjectEditModal'
import { useAuth } from '@/hooks/useAuth'

// Types

interface SortConfig {
  key: string
  direction: 'asc' | 'desc' | null
}

interface ProjectFilters {
  // Basic Search
  search: string
  
  // Location Filters
  city: string
  district: string
  neighbourhood: string
  area: string
  
  // Project Status & Type
  status: string
  typology: string
  sold_out: boolean | null
  radar: boolean | null
  
  // Team Filters
  architect_id: number | null | undefined
  developer_id: number | null | undefined
  broker_id: number | null | undefined
  
  // Amenities Filters
  has_parking: boolean | null
  has_pool: boolean | null
  has_gym: boolean | null
  
  // Unit Count Filters
  min_units: number | null
  max_units: number | null
  min_sold_units: number | null
  max_sold_units: number | null
  
  // Price Range Filters
  min_avg_price: number | null
  max_avg_price: number | null
  min_sqm_price: number | null
  max_sqm_price: number | null
  
  // Date Filters
  listing_date_from: string
  listing_date_to: string
  sold_out_date_from: string
  sold_out_date_to: string
  last_scraped_from: string
  last_scraped_to: string
  
  // Conversion Rate Filters
  min_completion_rate: number | null
  max_completion_rate: number | null
  
  // Scraping Status
  scrap_status: string
  has_address: boolean | null
}


const STRING_FILTER_KEYS = [
  'search',
  'city',
  'district',
  'neighbourhood',
  'area',
  'status',
  'typology',
  'listing_date_from',
  'listing_date_to',
  'sold_out_date_from',
  'sold_out_date_to',
  'last_scraped_from',
  'last_scraped_to',
  'scrap_status',
] as const
type StringFilterKey = typeof STRING_FILTER_KEYS[number]

const NUMERIC_FILTER_KEYS = [
  'min_units',
  'max_units',
  'min_sold_units',
  'max_sold_units',
  'min_avg_price',
  'max_avg_price',
  'min_sqm_price',
  'max_sqm_price',
  'min_completion_rate',
  'max_completion_rate',
] as const
type NumericFilterKey = typeof NUMERIC_FILTER_KEYS[number]

const BOOLEAN_FILTER_KEYS = ['sold_out', 'radar', 'has_parking', 'has_pool', 'has_gym'] as const
type BooleanFilterKey = typeof BOOLEAN_FILTER_KEYS[number]

const MEMBER_FILTER_KEYS = ['architect_id', 'developer_id', 'broker_id'] as const

const createDefaultFilters = (): ProjectFilters => ({
  search: '',
  city: '',
  district: '',
  neighbourhood: '',
  area: '',
  status: '',
  typology: '',
  sold_out: null,
  radar: null,
  architect_id: undefined,
  developer_id: undefined,
  broker_id: undefined,
  has_parking: null,
  has_pool: null,
  has_gym: null,
  min_units: null,
  max_units: null,
  min_sold_units: null,
  max_sold_units: null,
  min_avg_price: null,
  max_avg_price: null,
  min_sqm_price: null,
  max_sqm_price: null,
  listing_date_from: '',
  listing_date_to: '',
  sold_out_date_from: '',
  sold_out_date_to: '',
  last_scraped_from: '',
  last_scraped_to: '',
  min_completion_rate: null,
  max_completion_rate: null,
  scrap_status: '',
  has_address: null,
})

const parseNumberParam = (value: string | null): number | null => {
  if (value === null || value.trim() === '') {
    return null
  }

  const parsed = Number(value)
  return Number.isNaN(parsed) ? null : parsed
}

const areFiltersEqual = (a: ProjectFilters, b: ProjectFilters) => {
  const keys = new Set<keyof ProjectFilters>([
    ...(Object.keys(a) as Array<keyof ProjectFilters>),
    ...(Object.keys(b) as Array<keyof ProjectFilters>),
  ])

  for (const key of keys) {
    if (a[key] !== b[key]) {
      return false
    }
  }

  return true
}

const parseFiltersFromSearchParams = (params: URLSearchParams): {
  filters: ProjectFilters
  page: number
  sort: SortConfig
} => {
  const filters = createDefaultFilters()

  STRING_FILTER_KEYS.forEach((key) => {
    const value = params.get(key)
    if (value !== null) {
      filters[key] = value as ProjectFilters[StringFilterKey]
    }
  })

  BOOLEAN_FILTER_KEYS.forEach((key) => {
    const value = params.get(key)
    if (value === 'true') {
      filters[key] = true as ProjectFilters[BooleanFilterKey]
    } else if (value === 'false') {
      filters[key] = false as ProjectFilters[BooleanFilterKey]
    } else if (value === 'null') {
      filters[key] = null as ProjectFilters[BooleanFilterKey]
    }
  })

  const hasAddressParam = params.get('has_address')
  if (hasAddressParam === 'true') {
    filters.has_address = true
  } else if (hasAddressParam === 'false') {
    filters.has_address = false
  } else if (hasAddressParam === 'null') {
    filters.has_address = null
  }

  MEMBER_FILTER_KEYS.forEach((key) => {
    const value = params.get(key)
    if (value === null) {
      return
    }

    if (value === 'null') {
      filters[key] = null
      return
    }

    const parsedId = Number(value)
    if (!Number.isNaN(parsedId)) {
      filters[key] = parsedId
    } else {
      filters[key] = undefined
    }
  })

  NUMERIC_FILTER_KEYS.forEach((key) => {
    const value = params.get(key)
    if (value !== null) {
      filters[key] = parseNumberParam(value) as ProjectFilters[NumericFilterKey]
    }
  })

  const pageParam = params.get('page')
  const parsedPage = pageParam ? Number(pageParam) : 1
  const page = Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1

  const sortKeyParam = params.get('sort') || ''
  const directionParam = params.get('direction')
  const sort: SortConfig = sortKeyParam && (directionParam === 'asc' || directionParam === 'desc')
    ? { key: sortKeyParam, direction: directionParam }
    : { key: '', direction: null }

  return {
    filters,
    page,
    sort,
  }
}

const buildSearchParamsFromState = (
  filters: ProjectFilters,
  page: number,
  sort: SortConfig,
): URLSearchParams => {
  const params = new URLSearchParams()

  if (page > 1) {
    params.set('page', String(page))
  }

  if (sort.key && sort.direction) {
    params.set('sort', sort.key)
    params.set('direction', sort.direction)
  }

  STRING_FILTER_KEYS.forEach((key) => {
    const value = filters[key]
    if (typeof value === 'string' && value.trim() !== '') {
      params.set(key, value)
    }
  })

  BOOLEAN_FILTER_KEYS.forEach((key) => {
    const value = filters[key]
    if (value !== null && value !== undefined) {
      params.set(key, String(value))
    }
  })

  if (filters.has_address !== null && filters.has_address !== undefined) {
    params.set('has_address', String(filters.has_address))
  }

  MEMBER_FILTER_KEYS.forEach((key) => {
    const value = filters[key]
    if (value === null) {
      params.set(key, 'null')
    } else if (typeof value === 'number') {
      params.set(key, String(value))
    }
  })

  NUMERIC_FILTER_KEYS.forEach((key) => {
    const value = filters[key]
    if (value !== null && value !== undefined && !Number.isNaN(value)) {
      params.set(key, String(value))
    }
  })

  return params
}


export function Projects() {
  const navigate = useNavigate()
  const location = useLocation()
  const { canExport, canMutate } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState<ProjectFilters>(createDefaultFilters)
  const [debouncedFilters, setDebouncedFilters] = useState<ProjectFilters>(createDefaultFilters)
  const [initializedFromParams, setInitializedFromParams] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [selectedProjects, setSelectedProjects] = useState<number[]>([])
  const [showFilters, setShowFilters] = useState(false)
  const [editingProject, setEditingProject] = useState<number | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(50) // Load 50 projects per page for better performance
  const [isExporting, setIsExporting] = useState(false)
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: null })

  const searchParamsString = useMemo(() => searchParams.toString(), [searchParams])

  useEffect(() => {
    const { filters: parsedFilters, page, sort } = parseFiltersFromSearchParams(searchParams)

    setFilters(prev => (areFiltersEqual(prev, parsedFilters) ? prev : parsedFilters))
    setDebouncedFilters(prev => (areFiltersEqual(prev, parsedFilters) ? prev : parsedFilters))
    setCurrentPage(prev => (prev === page ? prev : page))
    setSortConfig(prev => (prev.key === sort.key && prev.direction === sort.direction ? prev : sort))

    if (!initializedFromParams) {
      setInitializedFromParams(true)
    }
  }, [searchParams, initializedFromParams])

  useEffect(() => {
    if (!initializedFromParams) {
      return
    }

    const params = buildSearchParamsFromState(debouncedFilters, currentPage, sortConfig)
    const nextString = params.toString()

    if (nextString !== searchParamsString) {
      setSearchParams(params, { replace: true })
    }
  }, [debouncedFilters, currentPage, sortConfig, initializedFromParams, searchParamsString, setSearchParams])

  // Debounced filters for API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters)
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [filters])

  const queryClient = useQueryClient()

  // Manual search function
  const handleSearch = useCallback(() => {
    setIsSearching(true)
    setDebouncedFilters(filters)
    setCurrentPage(1) // Reset to first page when searching
    setTimeout(() => setIsSearching(false), 1000) // Show loading state briefly
  }, [filters])

  // Handle filter changes with immediate UI update
  const handleFilterChange = useCallback((key: keyof ProjectFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [])

  const navigateToProjectDetail = useCallback((projectId: number) => {
    navigate(`/projects/${projectId}`, {
      state: {
        fromProjects: {
          search: location.search,
        },
      },
    })
  }, [navigate, location.search])

  // Fetch projects with filtering and pagination
  const { data: projectsResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['projects', debouncedFilters, currentPage, pageSize, sortConfig],
    queryFn: async () => {
      try {
        console.log('Fetching projects with filters:', debouncedFilters, 'page:', currentPage, 'sort:', sortConfig);
        const params = new URLSearchParams()
        params.append('page', currentPage.toString())
        params.append('limit', pageSize.toString())
        
        // Sorting parameters
        if (sortConfig.key && sortConfig.direction) {
          params.append('sortBy', sortConfig.key)
          params.append('sortOrder', sortConfig.direction)
        }
        
        // Basic search
        if (debouncedFilters.search) params.append('search', debouncedFilters.search)
        
        // Location filters
        if (debouncedFilters.status) params.append('status', debouncedFilters.status)
        if (debouncedFilters.city) params.append('city', debouncedFilters.city)
        if (debouncedFilters.district) params.append('district', debouncedFilters.district)
        if (debouncedFilters.neighbourhood) params.append('neighbourhood', debouncedFilters.neighbourhood)
        if (debouncedFilters.area) params.append('area', debouncedFilters.area)
        if (debouncedFilters.has_address !== null && debouncedFilters.has_address !== undefined) {
          params.append('has_address', debouncedFilters.has_address.toString())
        }
        
        // Project type and status
        if (debouncedFilters.typology) params.append('typology', debouncedFilters.typology)
        if (debouncedFilters.sold_out !== null) params.append('sold_out', debouncedFilters.sold_out.toString())
        if (debouncedFilters.radar !== null) params.append('radar', debouncedFilters.radar.toString())
        
        // Team filters
        if (debouncedFilters.architect_id !== undefined) {
          if (debouncedFilters.architect_id === null) {
            params.append('architect_id', 'null')
          } else {
            params.append('architect_id', debouncedFilters.architect_id.toString())
          }
        }
        if (debouncedFilters.developer_id !== undefined) {
          if (debouncedFilters.developer_id === null) {
            params.append('developer_id', 'null')
          } else {
            params.append('developer_id', debouncedFilters.developer_id.toString())
          }
        }
        if (debouncedFilters.broker_id !== undefined) {
          if (debouncedFilters.broker_id === null) {
            params.append('broker_id', 'null')
          } else {
            params.append('broker_id', debouncedFilters.broker_id.toString())
          }
        }
        
        // Amenities filters
        if (debouncedFilters.has_parking !== null) params.append('has_parking', debouncedFilters.has_parking.toString())
        if (debouncedFilters.has_pool !== null) params.append('has_pool', debouncedFilters.has_pool.toString())
        if (debouncedFilters.has_gym !== null) params.append('has_gym', debouncedFilters.has_gym.toString())
        
        // Unit count filters
        if (debouncedFilters.min_units) params.append('min_units', debouncedFilters.min_units.toString())
        if (debouncedFilters.max_units) params.append('max_units', debouncedFilters.max_units.toString())
        if (debouncedFilters.min_sold_units) params.append('min_sold_units', debouncedFilters.min_sold_units.toString())
        if (debouncedFilters.max_sold_units) params.append('max_sold_units', debouncedFilters.max_sold_units.toString())
        
        // Price range filters
        if (debouncedFilters.min_avg_price) params.append('min_avg_price', debouncedFilters.min_avg_price.toString())
        if (debouncedFilters.max_avg_price) params.append('max_avg_price', debouncedFilters.max_avg_price.toString())
        if (debouncedFilters.min_sqm_price) params.append('min_sqm_price', debouncedFilters.min_sqm_price.toString())
        if (debouncedFilters.max_sqm_price) params.append('max_sqm_price', debouncedFilters.max_sqm_price.toString())
        
        // Date filters
        if (debouncedFilters.listing_date_from) params.append('listing_date_from', debouncedFilters.listing_date_from)
        if (debouncedFilters.listing_date_to) params.append('listing_date_to', debouncedFilters.listing_date_to)
        if (debouncedFilters.sold_out_date_from) params.append('sold_out_date_from', debouncedFilters.sold_out_date_from)
        if (debouncedFilters.sold_out_date_to) params.append('sold_out_date_to', debouncedFilters.sold_out_date_to)
        if (debouncedFilters.last_scraped_from) params.append('last_scraped_from', debouncedFilters.last_scraped_from)
        if (debouncedFilters.last_scraped_to) params.append('last_scraped_to', debouncedFilters.last_scraped_to)
        
        // Completion rate filters
        if (debouncedFilters.min_completion_rate) params.append('min_completion_rate', debouncedFilters.min_completion_rate.toString())
        if (debouncedFilters.max_completion_rate) params.append('max_completion_rate', debouncedFilters.max_completion_rate.toString())
        
        // Scraping status
        if (debouncedFilters.scrap_status) params.append('scrap_status', debouncedFilters.scrap_status)

        const response = await api.get(`/projects?${params}`)
        console.log('Projects response:', response.data);

        return response.data
      } catch (error) {
        console.error('Error fetching projects:', error);
        throw error;
      }
    },
    retry: 3,
    retryDelay: 1000,
  })

  // (Removed) Portfolio Typology Statistics section and related query

  // Fetch team data for filters
  const { data: architects } = useQuery({
    queryKey: ['architects'],
    queryFn: async () => {
      const response = await api.get('/architects/dropdown')
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const { data: developers } = useQuery({
    queryKey: ['developers'],
    queryFn: async () => {
      const response = await api.get('/developers/dropdown')
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const { data: brokers } = useQuery({
    queryKey: ['brokers'],
    queryFn: async () => {
      const response = await api.get('/brokers/dropdown')
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Predefined typology options
  const typologyOptions = [
    { value: 'Apartment', label: 'Apartment' },
    { value: 'House', label: 'House' },
    { value: 'Apartment & House', label: 'Apartment & House' },
    { value: 'Tourist Apartment', label: 'Tourist Apartment' }
  ]

  const projects = projectsResponse?.data || []
  const totalProjects = projectsResponse?.meta?.total || 0
  const totalPages = Math.ceil(totalProjects / pageSize)


  // Projects are already filtered and sorted server-side, just validate and sanitize data
  const processedProjects = useMemo(() => {
    return projects.map(project => ({
      ...project,
      name: project.name || 'Unnamed Project',
      status: project.status || 'unknown',
      city: project.city || 'Unknown City',
      district: project.district || 'Unknown District',
      website: project.website || '',
      sold_units: project.sold_units || 0,
      total_units: project.total_units || 0,
      reserved_units: project.reserved_units || 0,
      avg_sqm_price: project.avg_sqm_price || 0,
      last_scraped_at: project.last_scraped_at || null,
    }))
  }, [projects])

  // Bulk delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (projectIds: number[]) => {
      await Promise.all(projectIds.map(id => api.delete(`/projects/${id}`)))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setSelectedProjects([])
      toast.success(`Deleted ${selectedProjects.length} projects`)
    },
    onError: () => {
      toast.error('Failed to delete projects')
    },
  })

  // Single delete mutation
  const singleDeleteMutation = useMutation({
    mutationFn: async (projectId: number) => {
      await api.delete(`/projects/${projectId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('Project deleted successfully')
    },
    onError: (error: any) => {
      console.error('Error deleting project:', error)
      toast.error('Failed to delete project')
    },
  })

  const handleSelectAll = () => {
      if (selectedProjects.length === processedProjects.length) {
        setSelectedProjects([])
      } else {
        setSelectedProjects(processedProjects.map(p => p.project_id))
      }
  }

  const handleSelectProject = (projectId: number) => {
    setSelectedProjects(prev =>
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    )
  }

  const handleSort = (key: string) => {
    setSortConfig(prev => {
      let newConfig: SortConfig
      if (prev.key === key) {
        // Cycle through: asc -> desc -> null
        if (prev.direction === 'asc') {
          newConfig = { key, direction: 'desc' as const }
        } else if (prev.direction === 'desc') {
          newConfig = { key: '', direction: null }
        } else {
          newConfig = { key, direction: 'asc' as const }
        }
      } else {
        // New sort key - start with ascending
        newConfig = { key, direction: 'asc' as const }
      }
      
      console.log(`Sorting by ${newConfig.key} ${newConfig.direction}`)
      return newConfig
    })
    
    // Reset to page 1 when sorting changes
    setCurrentPage(1)
  }

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key) {
      return <ChevronsUpDown className="h-4 w-4 text-secondary-400" />
    }
    if (sortConfig.direction === 'asc') {
      return <ChevronUp className="h-4 w-4 text-primary-600" />
    }
    if (sortConfig.direction === 'desc') {
      return <ChevronDown className="h-4 w-4 text-primary-600" />
    }
    return <ChevronsUpDown className="h-4 w-4 text-secondary-400" />
  }

  const handleBulkDelete = () => {
    if (selectedProjects.length > 0) {
      deleteMutation.mutate(selectedProjects)
    }
  }

  const handleSingleDelete = (projectId: number, projectName: string) => {
    if (window.confirm(`Are you sure you want to delete "${projectName}"? This action cannot be undone.`)) {
      singleDeleteMutation.mutate(projectId)
    }
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const params = new URLSearchParams()

      // Apply all current filters to export
      if (filters.search) params.append('search', filters.search)
      if (filters.status) params.append('status', filters.status)
      if (filters.city) params.append('city', filters.city)
      if (filters.district) params.append('district', filters.district)
      if (filters.neighbourhood) params.append('neighbourhood', filters.neighbourhood)
      if (filters.area) params.append('area', filters.area)
      if (filters.has_address !== null) params.append('has_address', filters.has_address.toString())
      if (filters.typology) params.append('typology', filters.typology)
      if (filters.sold_out !== null) params.append('sold_out', filters.sold_out.toString())
      if (filters.radar !== null) params.append('radar', filters.radar.toString())
      if (filters.architect_id) params.append('architect_id', filters.architect_id.toString())
      if (filters.developer_id) params.append('developer_id', filters.developer_id.toString())
      if (filters.broker_id) params.append('broker_id', filters.broker_id.toString())
      if (filters.has_parking !== null) params.append('has_parking', filters.has_parking.toString())
      if (filters.has_pool !== null) params.append('has_pool', filters.has_pool.toString())
      if (filters.has_gym !== null) params.append('has_gym', filters.has_gym.toString())
      if (filters.min_units) params.append('min_units', filters.min_units.toString())
      if (filters.max_units) params.append('max_units', filters.max_units.toString())
      if (filters.min_sold_units) params.append('min_sold_units', filters.min_sold_units.toString())
      if (filters.max_sold_units) params.append('max_sold_units', filters.max_sold_units.toString())
      if (filters.min_avg_price) params.append('min_avg_price', filters.min_avg_price.toString())
      if (filters.max_avg_price) params.append('max_avg_price', filters.max_avg_price.toString())
      if (filters.min_sqm_price) params.append('min_sqm_price', filters.min_sqm_price.toString())
      if (filters.max_sqm_price) params.append('max_sqm_price', filters.max_sqm_price.toString())
      if (filters.listing_date_from) params.append('listing_date_from', filters.listing_date_from)
      if (filters.listing_date_to) params.append('listing_date_to', filters.listing_date_to)
      if (filters.sold_out_date_from) params.append('sold_out_date_from', filters.sold_out_date_from)
      if (filters.sold_out_date_to) params.append('sold_out_date_to', filters.sold_out_date_to)
      if (filters.last_scraped_from) params.append('last_scraped_from', filters.last_scraped_from)
      if (filters.last_scraped_to) params.append('last_scraped_to', filters.last_scraped_to)
      if (filters.min_completion_rate) params.append('min_completion_rate', filters.min_completion_rate.toString())
      if (filters.max_completion_rate) params.append('max_completion_rate', filters.max_completion_rate.toString())
      if (filters.scrap_status) params.append('scrap_status', filters.scrap_status)

      const response = await api.get(`/export/projects/csv?${params}`, {
        responseType: 'blob',
      })

      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `projects_export_${new Date().toISOString().split('T')[0]}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.success('Projects exported successfully with all applied filters')
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export projects')
    } finally {
      setIsExporting(false)
    }
  }

  const clearFilters = useCallback(() => {
    const emptyFilters = createDefaultFilters()
    setFilters(emptyFilters)
    setDebouncedFilters(emptyFilters)
    setCurrentPage(1)
    setSortConfig({ key: '', direction: null })
  }, [])

  // Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="card p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Projects</h3>
          <p className="text-red-600 mb-4">
            {error instanceof Error ? error.message : 'An unexpected error occurred while loading projects'}
          </p>
          <button 
            onClick={() => refetch()} 
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Filters Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 space-y-6">
        <div className="bg-white rounded-xl shadow-tagus border border-secondary-200 p-5">
           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="form-group">
              <label className="form-label text-xs uppercase tracking-wider font-semibold text-secondary-500 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  className="form-input pl-10 h-10 bg-secondary-50 border-transparent focus:bg-white transition-colors"
                  placeholder="Search projects..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-input"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="sold_out">Sold Out</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">City</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter by city..."
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">District</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter by district..."
                value={filters.district}
                onChange={(e) => handleFilterChange('district', e.target.value)}
              />
            </div>
          </div>

          {/* Advanced Filters - Collapsible */}
          {showFilters && (
            <div className="space-y-6 p-6 bg-secondary-50 rounded-lg border">
              {/* Location Filters */}
              <div>
                <h3 className="text-sm font-medium text-secondary-900 mb-3">Location Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="form-group">
                    <label className="form-label">Neighbourhood</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Filter by neighbourhood..."
                      value={filters.neighbourhood}
                      onChange={(e) => handleFilterChange('neighbourhood', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Area</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Filter by area..."
                      value={filters.area}
                      onChange={(e) => handleFilterChange('area', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Typology</label>
                    <select
                      className="form-input"
                      value={filters.typology || ''}
                      onChange={(e) => handleFilterChange('typology', e.target.value === '' ? '' : e.target.value)}
                    >
                      <option value="">All Typologies</option>
                      {typologyOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Address Status</label>
                    <select
                      className="form-input"
                      value={filters.has_address === null ? '' : filters.has_address ? 'with' : 'without'}
                      onChange={(e) => {
                        const value = e.target.value
                        if (value === '') {
                          handleFilterChange('has_address', null)
                        } else if (value === 'with') {
                          handleFilterChange('has_address', true)
                        } else {
                          handleFilterChange('has_address', false)
                        }
                      }}
                    >
                      <option value="">All addresses</option>
                      <option value="with">With address</option>
                      <option value="without">Without address</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Project Status & Features */}
              <div>
                <h3 className="text-sm font-medium text-secondary-900 mb-3">Project Status & Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="form-group">
                    <label className="form-label">Sold Out</label>
                    <select
                      className="form-input"
                      value={filters.sold_out === null ? '' : filters.sold_out.toString()}
                      onChange={(e) => handleFilterChange('sold_out', e.target.value === '' ? null : e.target.value === 'true')}
                    >
                      <option value="">All</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">In Radar</label>
                    <select
                      className="form-input"
                      value={filters.radar === null ? '' : filters.radar.toString()}
                      onChange={(e) => handleFilterChange('radar', e.target.value === '' ? null : e.target.value === 'true')}
                    >
                      <option value="">All</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Has Parking</label>
                    <select
                      className="form-input"
                      value={filters.has_parking === null ? '' : filters.has_parking.toString()}
                      onChange={(e) => handleFilterChange('has_parking', e.target.value === '' ? null : e.target.value === 'true')}
                    >
                      <option value="">All</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Has Pool</label>
                    <select
                      className="form-input"
                      value={filters.has_pool === null ? '' : filters.has_pool.toString()}
                      onChange={(e) => handleFilterChange('has_pool', e.target.value === '' ? null : e.target.value === 'true')}
                    >
                      <option value="">All</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Has Gym</label>
                    <select
                      className="form-input"
                      value={filters.has_gym === null ? '' : filters.has_gym.toString()}
                      onChange={(e) => handleFilterChange('has_gym', e.target.value === '' ? null : e.target.value === 'true')}
                    >
                      <option value="">All</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Team Filters */}
              <div>
                <h3 className="text-sm font-medium text-secondary-900 mb-3">Team Members</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className="form-label">Architect</label>
                    <select
                      className="form-input"
                      value={filters.architect_id === null ? 'empty' : filters.architect_id || ''}
                      onChange={(e) => {
                        if (e.target.value === 'empty') {
                          handleFilterChange('architect_id', null)
                        } else {
                          handleFilterChange('architect_id', e.target.value === '' ? undefined : Number(e.target.value))
                        }
                      }}
                    >
                      <option value="">All Architects</option>
                      <option value="empty">No Architect Assigned</option>
                      {architects?.data?.map((architect) => (
                        <option key={architect.architect_id} value={architect.architect_id}>
                          {architect.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Developer</label>
                    <select
                      className="form-input"
                      value={filters.developer_id === null ? 'empty' : filters.developer_id || ''}
                      onChange={(e) => {
                        if (e.target.value === 'empty') {
                          handleFilterChange('developer_id', null)
                        } else {
                          handleFilterChange('developer_id', e.target.value === '' ? undefined : Number(e.target.value))
                        }
                      }}
                    >
                      <option value="">All Developers</option>
                      <option value="empty">No Developer Assigned</option>
                      {developers?.data?.map((developer) => (
                        <option key={developer.developer_id} value={developer.developer_id}>
                          {developer.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Broker</label>
                    <select
                      className="form-input"
                      value={filters.broker_id === null ? 'empty' : filters.broker_id || ''}
                      onChange={(e) => {
                        if (e.target.value === 'empty') {
                          handleFilterChange('broker_id', null)
                        } else {
                          handleFilterChange('broker_id', e.target.value === '' ? undefined : Number(e.target.value))
                        }
                      }}
                    >
                      <option value="">All Brokers</option>
                      <option value="empty">No Broker Assigned</option>
                      {brokers?.data?.map((broker) => (
                        <option key={broker.broker_id} value={broker.broker_id}>
                          {broker.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Unit Count Filters */}
              <div>
                <h3 className="text-sm font-medium text-secondary-900 mb-3">Unit Counts</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="form-group">
                    <label className="form-label">Min Units</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0"
                      value={filters.min_units || ''}
                      onChange={(e) => handleFilterChange('min_units', Number(e.target.value) || null)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Max Units</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="1000"
                      value={filters.max_units || ''}
                      onChange={(e) => handleFilterChange('max_units', Number(e.target.value) || null)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Min Sold Units</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0"
                      value={filters.min_sold_units || ''}
                      onChange={(e) => handleFilterChange('min_sold_units', Number(e.target.value) || null)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Max Sold Units</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="1000"
                      value={filters.max_sold_units || ''}
                      onChange={(e) => handleFilterChange('max_sold_units', Number(e.target.value) || null)}
                    />
                  </div>
                </div>
              </div>

              {/* Price Range Filters */}
              <div>
                <h3 className="text-sm font-medium text-secondary-900 mb-3">Price Ranges</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="form-group">
                    <label className="form-label">Min Avg Price (€)</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0"
                      value={filters.min_avg_price || ''}
                      onChange={(e) => handleFilterChange('min_avg_price', Number(e.target.value) || null)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Max Avg Price (€)</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="1000000"
                      value={filters.max_avg_price || ''}
                      onChange={(e) => handleFilterChange('max_avg_price', Number(e.target.value) || null)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Min Price/m² (€)</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0"
                      value={filters.min_sqm_price || ''}
                      onChange={(e) => handleFilterChange('min_sqm_price', Number(e.target.value) || null)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Max Price/m² (€)</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="10000"
                      value={filters.max_sqm_price || ''}
                      onChange={(e) => handleFilterChange('max_sqm_price', Number(e.target.value) || null)}
                    />
                  </div>
                </div>
              </div>

              {/* Date Filters */}
              <div>
                <h3 className="text-sm font-medium text-secondary-900 mb-3">Date Ranges</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className="form-label">Launch Date From</label>
                    <input
                      type="date"
                      className="form-input"
                      value={filters.listing_date_from}
                      onChange={(e) => handleFilterChange('listing_date_from', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Launch Date To</label>
                    <input
                      type="date"
                      className="form-input"
                      value={filters.listing_date_to}
                      onChange={(e) => handleFilterChange('listing_date_to', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Sold Out Date From</label>
                    <input
                      type="date"
                      className="form-input"
                      value={filters.sold_out_date_from}
                      onChange={(e) => handleFilterChange('sold_out_date_from', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Sold Out Date To</label>
                    <input
                      type="date"
                      className="form-input"
                      value={filters.sold_out_date_to}
                      onChange={(e) => handleFilterChange('sold_out_date_to', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Scraped From</label>
                    <input
                      type="date"
                      className="form-input"
                      value={filters.last_scraped_from}
                      onChange={(e) => handleFilterChange('last_scraped_from', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Scraped To</label>
                    <input
                      type="date"
                      className="form-input"
                      value={filters.last_scraped_to}
                      onChange={(e) => handleFilterChange('last_scraped_to', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Conversion Rate Filters */}
              <div>
                <h3 className="text-sm font-medium text-secondary-900 mb-3">Performance Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="form-group">
                    <label className="form-label">Min Conversion (%)</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0"
                      step="0.1"
                      value={filters.min_completion_rate || ''}
                      onChange={(e) => handleFilterChange('min_completion_rate', Number(e.target.value) || null)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Max Conversion (%)</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="100"
                      step="0.1"
                      value={filters.max_completion_rate || ''}
                      onChange={(e) => handleFilterChange('max_completion_rate', Number(e.target.value) || null)}
                    />
                  </div>
                </div>
              </div>

              {/* Scraping Status */}
              <div>
                <h3 className="text-sm font-medium text-secondary-900 mb-3">Data Quality</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className="form-label">Scraping Status</label>
                    <select
                      className="form-input"
                      value={filters.scrap_status}
                      onChange={(e) => handleFilterChange('scrap_status', e.target.value)}
                    >
                      <option value="">All Statuses</option>
                      <option value="successful">Successful</option>
                      <option value="http_error">HTTP Error</option>
                      <option value="connection_error">Connection Error</option>
                      <option value="other_error">Other Error</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filter Action Buttons */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mt-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleSearch}
                disabled={isSearching || isLoading}
                className="btn-primary flex items-center justify-center gap-2"
              >
                {isSearching ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                {isSearching ? 'Searching...' : 'Search'}
              </button>
              
              <button
                onClick={clearFilters}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <X className="h-4 w-4" />
                Clear Filters
              </button>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="btn-outline flex items-center gap-2"
              >
                <Filter className="h-4 w-4" />
                {showFilters ? 'Hide' : 'Show'} Advanced Filters
              </button>
            </div>
          </div>
        </div>

      {/* Bulk Actions */}
      {selectedProjects.length > 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-primary-800 font-medium">
              {selectedProjects.length} projects selected
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBulkDelete}
                className="btn-danger flex items-center"
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected
              </button>
              <button
                onClick={() => setSelectedProjects([])}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Projects Table */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
            <h3 className="text-xl font-semibold text-secondary-900">Projects List</h3>
            <div className="text-sm text-secondary-600">
              {totalProjects.toLocaleString()} projects • Page {currentPage} of {totalPages}
              {sortConfig.key && sortConfig.direction && (
                <span className="ml-2 text-primary-600">
                  • Sorted by {{ name: 'name', status: 'status', city: 'city', district: 'district', sold_units: 'units sold', avg_sqm_price: 'avg price/m²', last_scraped_at: 'last updated' }[sortConfig.key] || sortConfig.key} ({
                    sortConfig.key === 'sold_units' || sortConfig.key === 'avg_sqm_price' || sortConfig.key === 'last_scraped_at'
                      ? (sortConfig.direction === 'asc' ? 'Low to High' : 'High to Low')
                      : (sortConfig.direction === 'asc' ? 'A-Z' : 'Z-A')
                  })
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 gap-2">
            {canMutate && (
              <button
                onClick={() => navigate('/projects/create')}
                className="btn-primary flex items-center justify-center"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Project
              </button>
            )}
            <button
              onClick={() => refetch()}
              className="btn-secondary flex items-center justify-center"
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {canExport && (
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="btn-primary flex items-center justify-center"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                {isExporting ? 'Exporting...' : 'Export Excel'}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Card View for small screens */}
        <div className="block lg:hidden">
          {/* Mobile Sort Dropdown */}
          <div className="mb-4 flex justify-between items-center">
            <h4 className="text-sm font-medium text-secondary-700">Sort by:</h4>
            <select
              value={`${sortConfig.key}-${sortConfig.direction || 'none'}`}
              onChange={(e) => {
                const [key, direction] = e.target.value.split('-')
                if (key && direction && direction !== 'none') {
                  setSortConfig({ key, direction: direction as 'asc' | 'desc' })
                } else {
                  setSortConfig({ key: '', direction: null })
                }
              }}
              className="form-input text-sm py-1 px-2 w-48"
            >
              <option value="-none">No sorting</option>
              <option value="name-asc">Project Name (A-Z)</option>
              <option value="name-desc">Project Name (Z-A)</option>
              <option value="city-asc">City (A-Z)</option>
              <option value="city-desc">City (Z-A)</option>
              <option value="status-asc">Status (A-Z)</option>
              <option value="status-desc">Status (Z-A)</option>
              <option value="sold_units-asc">Units Sold (Low to High)</option>
              <option value="sold_units-desc">Units Sold (High to Low)</option>
              <option value="avg_sqm_price-asc">Price/m² (Low to High)</option>
              <option value="avg_sqm_price-desc">Price/m² (High to Low)</option>
              <option value="last_scraped_at-asc">Last Updated (Oldest First)</option>
              <option value="last_scraped_at-desc">Last Updated (Newest First)</option>
            </select>
          </div>
          
          {processedProjects.map((project) => (
            <div 
              key={project.project_id} 
              className="border border-secondary-200 rounded-lg p-4 mb-4 hover:bg-secondary-50 transition-colors cursor-pointer"
              onClick={() => navigateToProjectDetail(project.project_id)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedProjects.includes(project.project_id)}
                    onChange={() => handleSelectProject(project.project_id)}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-medium text-secondary-900 truncate">
                      {project.name || 'Unnamed Project'}
                    </h4>
                    <p className="text-xs text-secondary-500 truncate">
                      {project.city || 'Unknown City'}, {project.district || 'Unknown District'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      navigateToProjectDetail(project.project_id)
                    }}
                    className="text-primary-600 hover:text-primary-900"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleSingleDelete(project.project_id, project.name || 'Unnamed Project')
                    }}
                    disabled={singleDeleteMutation.isPending}
                    className="text-danger-600 hover:text-danger-900 disabled:opacity-50"
                    title="Delete Project"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-secondary-500">Status:</span>
                  <span className={`ml-1 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    project.status === 'active' ? 'bg-success-100 text-success-800' :
                    project.status === 'sold_out' ? 'bg-danger-100 text-danger-800' :
                    project.status === 'pending' ? 'bg-warning-100 text-warning-800' : 'bg-secondary-100 text-secondary-800'
                  }`}>
                    {project.status ? project.status.replace('_', ' ') : 'Unknown'}
                  </span>
                </div>
                <div>
                  <span className="text-secondary-500">Units:</span>
                  <span className="ml-1 font-medium">{project.sold_units || 0}/{project.total_units || 0}</span>
                  <span className="ml-1 text-secondary-500">
                    ({(project.total_units || 0) > 0 ? Math.round(((Number(project.sold_units) + Number(project.reserved_units || 0)) / Number(project.total_units || 1)) * 100) : 0}%)
                  </span>
                </div>
                <div>
                  <span className="text-secondary-500">Price/m²:</span>
                  <span className="ml-1 font-medium">
                    {project.avg_sqm_price ? new Intl.NumberFormat('pt-PT', {
                      style: 'currency',
                      currency: 'EUR',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(project.avg_sqm_price) : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-secondary-500">Amenities:</span>
                  <div className="flex items-center space-x-1 mt-1">
                    {project.has_parking && <span title="Parking">🅿️</span>}
                    {project.has_pool && <span title="Pool">🏊</span>}
                    {project.has_gym && <span title="Gym">💪</span>}
                    {!project.has_parking && !project.has_pool && !project.has_gym && (
                      <span className="text-gray-400">None</span>
                    )}
                  </div>
                </div>
              </div>
              
              {project.website && (
                <div className="mt-2">
                  <a 
                    href={project.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-primary-600 hover:text-primary-800 truncate block"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {project.website}
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Desktop Table View for large screens */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-[1200px] w-full divide-y divide-secondary-200 table-fixed">
            <thead className="bg-secondary-50 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left w-12">
                  <input
                    type="checkbox"
                    checked={selectedProjects.length === processedProjects.length && processedProjects.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100 select-none transition-colors duration-200 w-full"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1 group">
                    <span>Project Details</span>
                    <div className="opacity-60 group-hover:opacity-100 transition-opacity">
                      {getSortIcon('name')}
                    </div>
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider w-40 cursor-pointer hover:bg-secondary-100 select-none transition-colors duration-200"
                  onClick={() => handleSort('city')}
                >
                  <div className="flex items-center space-x-1 group">
                    <span>Location</span>
                    <div className="opacity-60 group-hover:opacity-100 transition-opacity">
                      {getSortIcon('city')}
                    </div>
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider w-28 cursor-pointer hover:bg-secondary-100 select-none transition-colors duration-200"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1 group">
                    <span>Status</span>
                    <div className="opacity-60 group-hover:opacity-100 transition-opacity">
                      {getSortIcon('status')}
                    </div>
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider w-24 cursor-pointer hover:bg-secondary-100 select-none transition-colors duration-200"
                  onClick={() => handleSort('sold_units')}
                >
                  <div className="flex items-center space-x-1 group">
                    <span>Units</span>
                    <div className="opacity-60 group-hover:opacity-100 transition-opacity">
                      {getSortIcon('sold_units')}
                    </div>
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider w-32 cursor-pointer hover:bg-secondary-100 select-none transition-colors duration-200"
                  onClick={() => handleSort('avg_sqm_price')}
                >
                  <div className="flex items-center space-x-1 group">
                    <span>Avg Price/m²</span>
                    <div className="opacity-60 group-hover:opacity-100 transition-opacity">
                      {getSortIcon('avg_sqm_price')}
                    </div>
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider w-28">
                  Amenities
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider w-32 cursor-pointer hover:bg-secondary-100 select-none transition-colors duration-200"
                  onClick={() => handleSort('last_scraped_at')}
                >
                  <div className="flex items-center space-x-1 group">
                    <span>Updated</span>
                    <div className="opacity-60 group-hover:opacity-100 transition-opacity">
                      {getSortIcon('last_scraped_at')}
                    </div>
                  </div>
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider w-24">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {processedProjects.map((project) => (
                <tr 
                  key={project.project_id} 
                  className="hover:bg-secondary-50 cursor-pointer"
                  onClick={() => navigateToProjectDetail(project.project_id)}
                >
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedProjects.includes(project.project_id)}
                      onChange={() => handleSelectProject(project.project_id)}
                      className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-secondary-900 truncate">
                        {project.name || 'Unnamed Project'}
                      </div>
                      <div className="text-xs text-secondary-500 truncate max-w-[200px]">
                        {project.website || 'No website'}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-sm text-secondary-900 truncate">{project.city || 'Unknown City'}</div>
                    <div className="text-xs text-secondary-500 truncate">{project.district || 'Unknown District'}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      project.status === 'active' ? 'bg-green-100 text-green-800' :
                      project.status === 'sold_out' ? 'bg-blue-100 text-blue-800' :
                      project.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {project.status ? project.status.replace('_', ' ') : 'Unknown'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm text-secondary-900">
                    <div className="font-medium">{project.sold_units || 0}/{project.total_units || 0}</div>
                    <div className="text-xs text-secondary-500">
                      {(project.total_units || 0) > 0 ? Math.round(((Number(project.sold_units) + Number(project.reserved_units || 0)) / Number(project.total_units || 1)) * 100) : 0}%
                    </div>
                  </td>
                  <td className="px-3 py-2 text-sm text-secondary-900">
                    <div className="font-medium">
                      {project.avg_sqm_price ? new Intl.NumberFormat('pt-PT', {
                        style: 'currency',
                        currency: 'EUR',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(project.avg_sqm_price) : 'N/A'}
                    </div>
                    <div className="text-xs text-secondary-500">
                      per m²
                    </div>
                  </td>
                  <td className="px-3 py-2 text-sm text-secondary-500">
                    <div className="flex items-center space-x-1">
                      {project.has_parking && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" title="Parking">
                          🅿️
                        </span>
                      )}
                      {project.has_pool && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-cyan-100 text-cyan-800" title="Pool">
                          🏊
                        </span>
                      )}
                      {project.has_gym && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" title="Gym">
                          💪
                        </span>
                      )}
                      {!project.has_parking && !project.has_pool && !project.has_gym && (
                        <span className="text-xs text-gray-400">None</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-sm text-secondary-500">
                    {project.last_scraped_at ? new Date(project.last_scraped_at).toLocaleDateString('en-GB') : 'Never'}
                  </td>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => navigateToProjectDetail(project.project_id)}
                        className="text-primary-600 hover:text-primary-900"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleSingleDelete(project.project_id, project.name || 'Unnamed Project')}
                        disabled={singleDeleteMutation.isPending}
                        className="text-danger-600 hover:text-danger-900 disabled:opacity-50"
                        title="Delete Project"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 py-3 border-t border-secondary-200 gap-3">
            <div className="text-xs text-secondary-500 text-center sm:text-left">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalProjects)} of {totalProjects} projects
            </div>
            <div className="flex items-center justify-center space-x-1">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 text-xs border border-secondary-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50"
              >
                Prev
              </button>

              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-2 py-1 text-xs border rounded ${
                        currentPage === pageNum
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'border-secondary-300 hover:bg-secondary-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-xs border border-secondary-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {processedProjects.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-secondary-100 rounded-full flex items-center justify-center">
              <Search className="h-8 w-8 text-secondary-400" />
            </div>
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No projects found</h3>
            <p className="text-secondary-500 mb-4">
              {Object.values(filters).some(v => v) ? 'Try adjusting your filters' : 'Get started by creating your first project'}
            </p>
            {Object.values(filters).some(v => v) && (
              <button onClick={clearFilters} className="btn-secondary">
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Edit Form Modal */}
      {editingProject && (
        <ProjectEditModal
          isOpen={!!editingProject}
          onClose={() => setEditingProject(null)}
          projectId={editingProject}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['projects'] })
            setEditingProject(null)
          }}
        />
      )}

      </div>
    </div>
  )
}
